import React from 'react';
import clsx from 'clsx';
import { Clock } from 'lucide-react';
import { ENTITLEMENTS_FALLBACK, perDayLabel } from '../../utils/entitlementsConfig';

/**
 * TimeBadge - Futuristic Time Remaining Display
 * 
 * Shows remaining Lounge time with visual urgency indicators.
 * Elite/Elite+ users see their daily allowance with crown icon.
 * 
 * @param {number|null} minutesLeft - Minutes remaining today
 * @param {boolean} isElite - Whether user has Elite subscription
 * @param {boolean} isElitePlus - Whether user has Elite+ subscription
 * @param {number|null} dailyCap - User's daily cap in minutes (from entitlements config)
 */
export function TimeBadge({ minutesLeft, isElite, isElitePlus, dailyCap }) {
  // Get the correct label based on tier
  const getTierLabel = () => {
    if (isElitePlus) {
      const mins = dailyCap || ENTITLEMENTS_FALLBACK.lounge_minutes_per_day.elite_plus;
      return `Elite+ • ${perDayLabel(mins)}`;
    }
    if (isElite) {
      const mins = dailyCap || ENTITLEMENTS_FALLBACK.lounge_minutes_per_day.elite;
      return `Elite • ${perDayLabel(mins)}`;
    }
    return null;
  };

  const tierLabel = getTierLabel();

  if (tierLabel) {
    return (
      <div 
        className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium uppercase tracking-wider"
        style={{
          backgroundColor: 'rgba(14,18,48,0.9)',
          borderColor: 'rgba(148,163,184,0.4)',
          color: '#FED7AA',
          boxShadow: '0 18px 45px rgba(15,23,42,0.9)'
        }}
      >
        <span className="text-lg">👑</span>
        <span>{tierLabel}</span>
      </div>
    );
  }

  const low = minutesLeft !== null && minutesLeft <= 3;
  const danger = minutesLeft !== null && minutesLeft <= 1;

  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-xs font-medium tracking-wide"
      style={{
        backgroundColor: 'rgba(14,18,48,0.9)',
        borderColor: danger ? 'rgba(247,115,115,0.8)' : low ? 'rgba(249,115,22,0.7)' : 'rgba(148,163,184,0.4)',
        boxShadow: danger ? '0 0 22px rgba(249,115,22,0.6)' : '0 18px 45px rgba(15,23,42,0.9)'
      }}
    >
      <span className="text-base">⏱</span>
      <span className="text-[0.7rem] uppercase tracking-wider text-slate-300">
        {minutesLeft === null ? 'Checking time…' : danger
          ? 'Less than 1 minute'
          : `${minutesLeft} min left today`}
      </span>
    </div>
  );
}

/**
 * StreakBadge - Lounge Time Display
 * 
 * Shows available lounge time. Streak functionality temporarily removed
 * from Lounge UI until dashboard streak is verified stable.
 */
export function StreakBadge({ current, best }) {
  return (
    <div 
      className="inline-flex items-center gap-3 rounded-full px-4 py-1.5 border"
      style={{
        backgroundColor: 'rgba(14,18,48,0.95)',
        borderColor: 'rgba(148,163,184,0.4)',
        boxShadow: '0 18px 45px rgba(15,23,42,0.9)'
      }}
    >
      <div className="relative h-7 w-7 flex items-center justify-center">
        <Clock className="w-5 h-5 text-purple-400" />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[0.65rem] uppercase tracking-wider text-slate-400">
          Lounge time
        </span>
        <span className="text-xs text-slate-100">
          Daily minutes reset each day
        </span>
      </div>
    </div>
  );
}
